import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BACKEND_TO_FRONTEND_UNMET_MAP } from '../../../constants/unmetNeeds';

import SettingsModal from './LocalView/SettingsModal';
import SystemOverviewSection from './LocalView/SystemOverviewSection';
import PriorityNeedSection from './LocalView/PriorityNeedSection';
import SitesTreemapSection from './LocalView/SitesTreemapSection';
import HospitalDistributionSection from './LocalView/HospitalDistributionSection';
import PhysicianDistributionSection from './LocalView/PhysicianDistributionSection';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);


const LocalView = ({
  selectedSystem, 
  priorityUnmetNeed, 
  systemProfileData, 
  keyUnmetNeedsData, 
  siteTreemapData,
  hospitalDistributionData,
  physicianDistributionData,
  handlePriorityNeedChange,
  unmetNeedsDisplayNames,
}) => {
  const [activeComparison, setActiveComparison] = useState('local');
  const [selectedSiteType, setSelectedSiteType] = useState('All');
  const [cciAdjusted, setCciAdjusted] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  if (!systemProfileData || !keyUnmetNeedsData || !hospitalDistributionData || !physicianDistributionData) {
    return <div className="loading-container">Preparing view data...</div>;
  }

  const profile = systemProfileData;
  const { key_unmet_needs: unmetNeedsApiData, trends: exacerbationTrendsApiData } = keyUnmetNeedsData;

  const comparisonAvgKey = activeComparison;
  const comparisonAvgSuffix = `${activeComparison}_avg`;
  const comparisonAvgLabel = activeComparison.charAt(0).toUpperCase() + activeComparison.slice(1);

  let systemPercentage = 'N/A';
  if (Array.isArray(unmetNeedsApiData) && priorityUnmetNeed) {
    const currentNeedData = unmetNeedsApiData.find(
      (apiItem) => (BACKEND_TO_FRONTEND_UNMET_MAP[apiItem.name] || apiItem.name) === priorityUnmetNeed
    );
    if (currentNeedData && typeof currentNeedData.system === 'number') {
      systemPercentage = currentNeedData.system.toFixed(1);
    }
  }

  let viewTitle = `Local Comparison for ${selectedSystem.name}`;
  if (activeComparison === 'state') {
    viewTitle = `State Comparison for ${selectedSystem.name}`;
  } else if (activeComparison === 'national') {
    viewTitle = `National Comparison for ${selectedSystem.name}`;
  } else {
    viewTitle = `Local System View for ${selectedSystem.name}`;
  }

  const handleComparisonChange = (level) => {
    setActiveComparison(level);
  };

  const handleCciAdjustmentToggle = () => {
    setCciAdjusted(!cciAdjusted);
    console.warn("CCI Adjustment toggle clicked, but API doesn't support adjustment yet.");
  };

  const toggleSettingsModal = () => {
    setShowSettingsModal(!showSettingsModal);
  };

  return (
    <div className="local-view-container">
      <SystemOverviewSection
        selectedSystem={selectedSystem}
        activeComparison={activeComparison}
        onComparisonChange={handleComparisonChange}
        onOpenSettings={toggleSettingsModal}
        profile={profile}
        comparisonAvgLabel={comparisonAvgLabel}
        comparisonAvgSuffix={comparisonAvgSuffix}
        cciAdjusted={cciAdjusted}
        unmetNeedsApiData={unmetNeedsApiData}
        unmetNeedsDisplayMap={BACKEND_TO_FRONTEND_UNMET_MAP}
        exacerbationTrendsApiData={exacerbationTrendsApiData}
      />

      <PriorityNeedSection
        priorityUnmetNeed={priorityUnmetNeed}
        unmetNeedsDisplayNames={unmetNeedsDisplayNames}
        onPriorityNeedChange={handlePriorityNeedChange}
        selectedSystemName={selectedSystem.name}
        systemPercentage={systemPercentage}
      />

      <div className="current-view-heading">
        <h2>{viewTitle}</h2>
      </div>

      <SitesTreemapSection
        selectedSystem={selectedSystem}
        priorityUnmetNeed={priorityUnmetNeed}
        siteTreemapData={siteTreemapData}
        selectedSiteType={selectedSiteType}
        onSiteTypeChange={setSelectedSiteType}
        cciAdjusted={cciAdjusted}
      />

      <HospitalDistributionSection
        hospitalDistributionData={hospitalDistributionData}
        priorityUnmetNeed={priorityUnmetNeed}
        comparisonAvgLabel={comparisonAvgLabel}
        comparisonAvgKey={comparisonAvgKey}
        cciAdjusted={cciAdjusted}
        selectedSystem={selectedSystem}
        siteTreemapData={siteTreemapData}
        selectedSiteType={selectedSiteType}
      />

      <PhysicianDistributionSection
        physicianDistributionData={physicianDistributionData}
        priorityUnmetNeed={priorityUnmetNeed}
        comparisonAvgLabel={comparisonAvgLabel}
        comparisonAvgKey={comparisonAvgKey}
        cciAdjusted={cciAdjusted}
        selectedSystem={selectedSystem}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={toggleSettingsModal}
        cciAdjusted={cciAdjusted}
        onToggleCciAdjustment={handleCciAdjustmentToggle}
      />
    </div>
  );
};

LocalView.propTypes = {
  selectedSystem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    patientCount: PropTypes.number,
  }).isRequired,
  priorityUnmetNeed: PropTypes.string.isRequired,
  systemProfileData: PropTypes.object,
  keyUnmetNeedsData: PropTypes.shape({
    key_unmet_needs: PropTypes.array,
    trends: PropTypes.array,
  }),
  siteTreemapData: PropTypes.array,
  hospitalDistributionData: PropTypes.shape({
    bins: PropTypes.array,
    counts: PropTypes.object,
    averages: PropTypes.object,
  }),
  physicianDistributionData: PropTypes.shape({
    bins: PropTypes.array,
    counts: PropTypes.object,
    averages: PropTypes.object,
  }),
  handlePriorityNeedChange: PropTypes.func.isRequired,
  unmetNeedsDisplayNames: PropTypes.array.isRequired,
};

export default LocalView;
